// src/pages/inicioOperaciones.js
import React, { useEffect, useRef, useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { FiTruck, FiRefreshCw, FiX } from "react-icons/fi";
import { useUserId } from "../UserIdContext";
import "./usuarios.css";

/* ================== THEME LIGERO ================== */

const DASH_STYLES = `
:root{
  --bg:#050c13;
  --panel:#0b1520;
  --panel-soft:#0d1a26;
  --stroke:#132433;
  --primary:#0FB872;
  --primary-soft:#13332a;
  --accent:#65f9a1;
  --glow:rgba(0,255,170,.16);
  --glow-soft:rgba(0,255,170,.08);
  --text:#f2f7ff;
  --muted:#8499b8;
  --radius:18px;
}

.dashboard-wrap{ padding:12px; color:var(--text); }
.dash-header{ display:flex; justify-content:space-between; align-items:flex-end; gap:12px; flex-wrap:wrap; margin-bottom:12px; }
.dash-title h1{ margin:0; font-size:clamp(22px,2vw,32px); font-weight:800; display:flex; align-items:center; gap:10px; }
.dash-title small{ color:var(--muted); }
.dash-actions{ display:flex; gap:10px; flex-wrap:wrap; }
.dash-btn{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:7px 12px;
  border-radius:12px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
  color:var(--text);
  cursor:pointer;
  font-size:13px;
}
.dash-btn svg{ font-size:16px; }

.cards-row{
  display:grid;
  grid-template-columns:repeat(4, minmax(0,1fr));
  gap:10px;
}

.dashboard-wrap .card{
  background:radial-gradient(600px 280px at -40% -60%, rgba(15,184,114,.16), transparent 60%), var(--panel);
  border-radius:var(--radius);
  border:2px solid #33cc99;
  padding:10px 12px;
  box-shadow:0 0 0 1px rgba(51,204,153,.25), 0 10px 24px var(--glow-soft), 0 18px 48px rgba(0,0,0,.45);
  display:flex;
  flex-direction:column;
  gap:8px;
  position:relative;
  isolation:isolate;
  overflow:visible;
}

.status-card{ min-height:0; }
.management-status-panel{
  width:min(100%, 320px);
  margin-bottom:16px;
}

.dashboard-wrap .card::before{
  content:"";
  position:absolute;
  inset:-12px;
  border-radius:calc(var(--radius) + 12px);
  background:radial-gradient(90% 120% at 50% 50%, var(--glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}

.donut-wrap{
  width:118px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
}
.donut-meter{
  width:118px;
  height:70px;
  overflow:visible;
}
.donut-label{
  margin-top:-4px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-direction:column;
  color:#ffffff;
  line-height:1.1;
}
.donut-value{
  font-size:16px;
  font-weight:800;
}

.dashboard-wrap .graph-panel{
  margin-top:0;
  padding:12px;
  border-radius:var(--radius);
  border:2px solid #33cc99;
  background:var(--panel-soft);
  box-shadow:0 0 0 1px rgba(51,204,153,.2), 0 10px 24px var(--glow-soft), 0 18px 48px rgba(0,0,0,.45);
  position:relative;
  isolation:isolate;
  overflow:visible;
}

.dashboard-wrap .graph-panel::before{
  content:"";
  position:absolute;
  inset:-12px;
  border-radius:calc(var(--radius) + 12px);
  background:radial-gradient(90% 120% at 50% 50%, var(--glow), transparent 72%);
  z-index:-1;
  pointer-events:none;
}

.section-block{
  margin-top:14px;
}

.dashboard-top-sections{
  display:flex;
  flex-direction:column;
}

.dashboard-top-sections .occupancy-section{
  order:1;
}

.dashboard-top-sections .positions-board-section{
  order:2;
}

.section-title{
  margin:0 0 10px 2px;
  font-size:clamp(22px,2vw,32px);
  font-weight:800;
  line-height:1.2;
}

.section-block > .graph-panel > .graph-header{
  display:none;
}

.graph-header{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:10px;
}

.graph-header h2{
  margin:0;
  font-size:16px;
  font-weight:800;
}

.graph-header small{
  color:var(--muted);
}

.graph-legend{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

.recibidos,
.cerrados{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:5px 9px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.03);
  color:var(--muted);
  font-size:11px;
}

.recibidos::before,
.cerrados::before{
  content:"";
  width:10px;
  height:10px;
  border-radius:999px;
}

.recibidos::before{
  background:#22c55e;
}

.cerrados::before{
  background:#38bdf8;
}

.flow-grid{
  display:grid;
  grid-template-columns:1fr;
  gap:10px;
}

.flow-card{
  padding:10px;
  border-radius:16px;
  border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.02);
}

.flow-chart{
  width:100%;
  height:250px;
}

.flow-card-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:10px;
}

.flow-card-head--legend{
  justify-content:flex-end;
}

.flow-card-head h3{
  margin:0;
  font-size:15px;
  font-weight:800;
}

.flow-card-head small{
  color:var(--muted);
}

.custom-tooltip{
  background:#020617;
  border-radius:10px;
  padding:8px 10px;
  border:1px solid rgba(148,163,184,.5);
  font-size:11px;
}

.truck-table{
  width:100%;
  border-collapse:collapse;
  font-size:12px;
}
.truck-table th,
.truck-table td{
  padding:7px 9px;
  border-bottom:1px solid rgba(148,163,184,.3);
  text-align:left;
}
.truck-table th{ color:var(--muted); font-weight:600; }
.truck-table td strong{ font-weight:700; }
.truck-table th:last-child,
.truck-table td:last-child{
  width:92px;
  white-space:nowrap;
}

.summary-layout{
  display:grid;
  grid-template-columns:minmax(0, 420px);
  gap:12px;
  align-items:start;
  justify-content:start;
}

.summary-panel{
  min-width:0;
}

.summary-panel{
  width:100%;
  max-width:420px;
  justify-self:start;
}

.summary-panel .graph-header{
  display:none;
}

.positions-table-wrap{
  width:100%;
  overflow:auto;
}

.positions-table-wrap--limited{
  max-height:296px;
  overflow-y:auto;
}

.positions-table-wrap--limited::-webkit-scrollbar{
  width:12px;
  height:12px;
}

.positions-table-wrap--limited::-webkit-scrollbar-track{
  background:rgba(255,255,255,.05);
  border-radius:999px;
}

.positions-table-wrap--limited::-webkit-scrollbar-thumb{
  background:linear-gradient(180deg, rgba(15,184,114,.85), rgba(56,189,248,.85));
  border-radius:999px;
  border:2px solid rgba(5,12,19,.75);
}

.positions-table{
  width:100%;
  min-width:720px;
  border-collapse:collapse;
  font-size:12px;
}

.positions-table th,
.positions-table td{
  padding:7px 10px;
  border-bottom:1px solid rgba(148,163,184,.22);
  border-right:1px solid rgba(148,163,184,.14);
}

.positions-table th:last-child,
.positions-table td:last-child{
  border-right:none;
}

.positions-table thead th{
  color:#dff7eb;
  font-weight:800;
  background:rgba(15,184,114,.12);
  white-space:nowrap;
  position:sticky;
  top:0;
  z-index:2;
}

.positions-table tbody td{
  color:#f2f7ff;
}

.positions-table tbody tr:hover td{
  background:rgba(255,255,255,.03);
}

.positions-table tfoot td{
  font-weight:800;
  background:rgba(15,184,114,.14);
  color:#dff7eb;
  position:sticky;
  bottom:0;
  z-index:2;
}

.positions-client{
  text-transform:uppercase;
  font-weight:700;
}

.positions-number{
  text-align:right;
  font-variant-numeric:tabular-nums;
}

.positions-empty{
  padding:10px 2px 4px;
  color:var(--muted);
}

.ocu-topbar{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:12px;
}

.ocu-topbar-note{
  color:var(--muted);
  font-size:12px;
}

.ocu-grid{
  display:grid;
  grid-template-columns:repeat(5, minmax(0,1fr));
  gap:12px;
}

.management-occupation-panel .ocu-card{ min-width:0; }
.management-occupation-panel .ocu-title{ overflow-wrap:anywhere; }

.ocu-card{
  background:radial-gradient(600px 280px at -40% -60%, rgba(56,189,248,.14), transparent 60%), var(--panel);
  border-radius:var(--radius);
  border:1px solid rgba(148,163,184,.2);
  padding:12px 14px;
  box-shadow:0 18px 48px rgba(0,0,0,.35);
  overflow:hidden;
}

.ocu-card.actionable{
  cursor:pointer;
  transition:transform .18s ease, border-color .18s ease, box-shadow .18s ease;
}

.ocu-card.actionable:hover{
  transform:translateY(-2px);
  border-color:rgba(56,189,248,.42);
  box-shadow:0 24px 54px rgba(0,0,0,.45);
}

.ocu-card.actionable:focus-visible{
  outline:0;
  border-color:rgba(15,184,114,.62);
  box-shadow:0 0 0 2px rgba(15,184,114,.18), 0 24px 54px rgba(0,0,0,.45);
}

.ocu-title{
  font-weight:800;
  font-size:14px;
  margin:0;
}

.ocu-sub{
  margin-top:4px;
  font-size:12px;
  color:var(--muted);
}

.ocu-metrics{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:10px;
  margin-top:12px;
}

.ocu-pct{
  font-size:28px;
  font-weight:900;
  letter-spacing:-.5px;
}

.ocu-mini{
  display:flex;
  flex-direction:column;
  gap:4px;
  font-size:12px;
}

.ocu-bar{
  width:100%;
  height:10px;
  border-radius:999px;
  background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.08);
  overflow:hidden;
  margin-top:10px;
}

.ocu-bar > div{
  height:100%;
  border-radius:999px;
  background:linear-gradient(90deg, rgba(15,184,114,.95), rgba(56,189,248,.95));
  width:0%;
}

.ocu-empty{
  padding:10px 2px 4px;
  color:var(--muted);
}

.icon-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px;
  height:36px;
  border-radius:14px;
  border:1px solid rgba(255,255,255,.1);
  background:rgba(255,255,255,.04);
  color:var(--text);
  cursor:pointer;
}

.icon-btn:hover{
  border-color:rgba(15,184,114,.55);
}

.modal-backdrop{
  position:fixed;
  inset:0;
  background:radial-gradient(160% 120% at 50% 10%, rgba(101,249,161,.06), transparent 45%), rgba(0,0,0,.7);
  backdrop-filter:blur(4px);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:9999;
  padding:16px;
}

.modal-card{
  width:min(520px, 96vw);
  border-radius:22px;
  border:1px solid rgba(42,71,90,.7);
  background:linear-gradient(180deg, rgba(16,26,34,.96), rgba(8,14,22,.94));
  box-shadow:0 36px 120px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.02) inset;
  overflow:hidden;
}

.modal-head{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:10px;
  padding:14px 16px;
  border-bottom:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0));
}

.modal-head strong{
  font-size:13px;
}

.modal-body{
  padding:16px 18px 18px;
  display:flex;
  flex-direction:column;
  gap:10px;
  background:radial-gradient(140% 140% at 4% 0%, rgba(101,249,161,.06), transparent 32%), var(--panel-soft);
}

.modal-actions{
  display:flex;
  justify-content:flex-end;
  gap:10px;
  padding:12px 16px;
  border-top:1px solid rgba(17,49,67,.8);
  background:linear-gradient(180deg, rgba(10,16,24,.98), rgba(10,16,24,.92));
  flex-wrap:wrap;
}

.ocu-detail-summary{
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  gap:10px;
}

.ocu-detail-kpi{
  border:1px solid rgba(255,255,255,.08);
  border-radius:16px;
  padding:12px;
  background:rgba(255,255,255,.03);
  display:flex;
  flex-direction:column;
  gap:4px;
}

.ocu-detail-kpi span{
  font-size:11px;
  color:var(--muted);
  text-transform:uppercase;
  letter-spacing:.06em;
}

.ocu-detail-kpi strong{
  font-size:22px;
  line-height:1;
}

.ocu-detail-note{
  font-size:12px;
  color:var(--muted);
}

.ocu-detail-state{
  padding:14px 16px;
  border-radius:16px;
  border:1px dashed rgba(255,255,255,.12);
  background:rgba(255,255,255,.02);
  color:var(--muted);
  font-size:13px;
}

.ocu-detail-state.error{
  border-color:rgba(255,90,103,.35);
  color:#ffd7dc;
  background:rgba(255,90,103,.08);
}

.ocu-detail-table-wrap{
  border:1px solid rgba(255,255,255,.08);
  border-radius:18px;
  overflow:auto;
  background:rgba(255,255,255,.02);
}

.ocu-detail-table{
  width:100%;
  border-collapse:collapse;
  border-spacing:0;
}

.ocu-detail-table th,
.ocu-detail-table td{
  padding:12px 14px;
  border-bottom:1px solid rgba(255,255,255,.08);
  text-align:left;
  font-size:13px;
}

.ocu-detail-table th{
  color:var(--muted);
  font-weight:700;
  background:rgba(255,255,255,.03);
}

.ocu-detail-table td:nth-child(2),
.ocu-detail-table th:nth-child(2),
.ocu-detail-table td:last-child,
.ocu-detail-table th:last-child{
  text-align:right;
}

.ocu-detail-table tbody tr:last-child td{
  border-bottom:0;
}

.ocu-detail-client{
  font-weight:700;
  color:var(--text);
}

.ocu-detail-excess{
  color:#ff5a67;
  font-weight:800;
}

@media (max-width: 900px){
  .cards-row{ grid-template-columns:repeat(2, minmax(0,1fr)); }
  .flow-grid{ grid-template-columns:1fr; }
  .ocu-grid{ grid-template-columns:repeat(2, minmax(0,1fr)); }
  .summary-layout{ grid-template-columns:1fr; }
}
@media (max-width: 640px){
  .cards-row{ grid-template-columns:1fr; }
  .ocu-grid{ grid-template-columns:1fr; }
  .ocu-detail-summary{ grid-template-columns:repeat(2, minmax(0,1fr)); }
}

@supports (container-type: inline-size){
  .management-occupation-panel{
    container-type:inline-size;
    container-name:management-occupation;
  }
  .management-occupation-panel .ocu-grid{ grid-template-columns:minmax(0,1fr); }
  @container management-occupation (min-width: 420px){
    .management-occupation-panel .ocu-grid{ grid-template-columns:repeat(2,minmax(0,1fr)); }
  }
  @container management-occupation (min-width: 650px){
    .management-occupation-panel .ocu-grid{ grid-template-columns:repeat(3,minmax(0,1fr)); }
  }
  @container management-occupation (min-width: 850px){
    .management-occupation-panel .ocu-grid{ grid-template-columns:repeat(4,minmax(0,1fr)); }
  }
  @container management-occupation (min-width: 1000px){
    .management-occupation-panel .ocu-grid{ grid-template-columns:repeat(5,minmax(0,1fr)); }
  }
}
`;

const DASH_STYLE_ID = "inicio-dashboard-styles";

/* Inject styles once */
function useDashStyles() {
  useEffect(() => {
    if (!document.getElementById(DASH_STYLE_ID)) {
      const tag = document.createElement("style");
      tag.id = DASH_STYLE_ID;
      tag.textContent = DASH_STYLES;
      document.head.appendChild(tag);
    }
    return () => {
      document.getElementById(DASH_STYLE_ID)?.remove();
    };
  }, []);
}

function Modal({ open, title, onClose, children, footer, width = "min(520px, 96vw)" }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal-card"
        style={{ width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <strong>{title}</strong>
          <button
            className="icon-btn"
            type="button"
            onClick={onClose}
            title="Cerrar"
          >
            <FiX />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-actions">{footer}</div>}
      </div>
    </div>
  );
}

/* ================== HELPERS ================== */

const api = createApiClient({
  baseURL:
    process.env.REACT_APP_OPS_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "https://dnbinderapi.pruebasistemasgenius.com",
  headers: { "Content-Type": "application/json" },
});

const fechaUTC = (d = new Date()) =>
  new Date(d).toISOString().slice(0, 10);

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Si hoy no tiene datos, usamos la fecha más reciente disponible en el rango
const pickReferenciaFecha = (docs, hoyIso) => {
  const fechas = docs
    .map((d) =>
      typeof d.fecha === "string"
        ? d.fecha.slice(0, 10)
        : fechaUTC(d.fecha || new Date())
    )
    .filter(Boolean)
    .sort();

  if (!fechas.length) return hoyIso;
  if (fechas.includes(hoyIso)) return hoyIso;

  // fecha más cercana hacia atrás
  const anteriores = fechas.filter((f) => f <= hoyIso);
  if (anteriores.length) return anteriores[anteriores.length - 1];

  // si no hay anteriores, agarramos la más próxima hacia adelante
  return fechas[0];
};

const fmtLabel = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const resolveDepositoActual = (deposito) =>
  String(deposito || "").trim().toLowerCase() === "binder2"
    ? "binder2"
    : "binder1";
const binderName = (deposito) =>
  resolveDepositoActual(deposito) === "binder2" ? "Binder 2" : "Binder 1";

const POSITION_BOARD_BASE_COLUMNS = [
  "NACIONAL",
  "FISCAL",
  "EA",
  "ANEXO CORREO",
];

const normalizePositionBoardText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const normalizePositionBoardColumn = (value = "") => {
  const normalized = normalizePositionBoardText(value);
  if (!normalized) return "SIN UBICACION";
  if (normalized === "NAC") return "NACIONAL";
  if (normalized === "FIS") return "FISCAL";
  if (normalized === "ANEXO") return "ANEXO CORREO";
  return normalized;
};

const orderPositionBoardColumns = (columns = []) => {
  const preferred = [...POSITION_BOARD_BASE_COLUMNS, "SIN UBICACION"];
  const unique = Array.from(new Set(columns.filter(Boolean)));

  return unique.sort((a, b) => {
    const ai = preferred.indexOf(a);
    const bi = preferred.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      const av = ai === -1 ? preferred.length + 100 : ai;
      const bv = bi === -1 ? preferred.length + 100 : bi;
      if (av !== bv) return av - bv;
    }
    return a.localeCompare(b, "es", { sensitivity: "base" });
  });
};

const normalizePositionBoardData = (data) => {
  const inputRows = Array.isArray(data?.rows) ? data.rows : [];
  const inputColumns = Array.isArray(data?.columns) ? data.columns : [];

  const rows = inputRows.map((row) => {
    const porUbicacion = {};

    Object.entries(row?.porUbicacion || {}).forEach(([key, value]) => {
      const normalizedKey = normalizePositionBoardColumn(key);
      porUbicacion[normalizedKey] =
        (Number(porUbicacion[normalizedKey]) || 0) + Number(value || 0);
    });

    return {
      ...row,
      porUbicacion,
      total: Number(row?.total || 0),
    };
  });

  const totals = {};

  rows.forEach((row) => {
    Object.entries(row.porUbicacion || {}).forEach(([key, value]) => {
      totals[key] = (Number(totals[key]) || 0) + Number(value || 0);
    });
  });

  const extraColumns = new Set();
  inputColumns.forEach((column) =>
    extraColumns.add(normalizePositionBoardColumn(column))
  );
  rows.forEach((row) =>
    Object.keys(row.porUbicacion || {}).forEach((column) =>
      extraColumns.add(column)
    )
  );
  Object.keys(totals).forEach((column) => extraColumns.add(column));

  const columns = orderPositionBoardColumns([
    ...POSITION_BOARD_BASE_COLUMNS,
    ...Array.from(extraColumns),
  ]);

  columns.forEach((column) => {
    if (totals[column] == null) totals[column] = 0;
  });

  const totalGeneral = rows.reduce(
    (acc, row) => acc + Number(row?.total || 0),
    0
  );

  return {
    columns,
    rows,
    totals,
    totalGeneral,
  };
};

/**
 * Determina el estado de UN camión dentro de una operación.
 * Usa primero los contenedores con camionIndex, y si no,
 * cae a los flags pendientes/finalizado del propio camión.
 */
const LS_KEY_OCU = "ops_ocupaciones_v1";
const buildOcupacionesStorageKey = (deposito) =>
  `${LS_KEY_OCU}:${resolveDepositoActual(deposito)}`;

function normalizeOcupacion(item, depositoFallback = "binder1") {
  if (!item) return null;
  return {
    _id:
      item._id ||
      item.id ||
      item.uuid ||
      `${item.nombre || item.sucursal || "sin-nombre"}-${item.ocupacionTotal || item.total || 0}`,
    deposito: resolveDepositoActual(item.deposito || depositoFallback),
    nombre: item.nombre || item.sucursal || "Sin nombre",
    ocupacionTotal: Number(item.ocupacionTotal ?? item.total ?? 0),
    ocupadas: Number(item.ocupadas ?? item.ocupacionOcupada ?? 0),
    visible: item.visible !== false,
  };
}

function readStoredOcupaciones(storageKey, deposito = "binder1") {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const arr = raw ? JSON.parse(raw) : [];
    return (arr || [])
      .map((item) => normalizeOcupacion(item, deposito))
      .filter(Boolean);
  } catch {
    return [];
  }
}

async function apiListOcupaciones(deposito) {
  const depositoActual = resolveDepositoActual(deposito);
  const params = { deposito: depositoActual, _ts: Date.now() };
  const tries = [
    () => api.get("/api/ocupacion", { params }),
    () => api.get("/api/ocupacion/listar", { params }),
    () => api.get("/api/ocupaciones", { params }),
    () => api.get("/api/ocupaciones/listar", { params }),
  ];

  for (const fn of tries) {
    try {
      const r = await fn();
      const arr = Array.isArray(r.data) ? r.data : r.data?.data || [];
      return arr
        .map((item) => normalizeOcupacion(item, depositoActual))
        .filter(Boolean);
    } catch {}
  }

  throw new Error("No se pudo listar ocupaciones.");
}

function getOcupacionMetrics(item) {
  const totalRaw = Number(item?.ocupacionTotal ?? 0);
  const ocupadasRaw = Number(item?.ocupadas ?? 0);
  const total = Number.isFinite(totalRaw) ? totalRaw : 0;
  const ocupadas = Number.isFinite(ocupadasRaw) ? ocupadasRaw : 0;
  const libres = Math.max(0, total - ocupadas);
  const excedente = Math.max(0, ocupadas - total);
  const pct = total > 0 ? Math.max(0, (ocupadas * 100) / total) : 0;

  return {
    total,
    ocupadas,
    libres,
    excedente,
    pct,
    pctBar: Math.min(100, pct),
  };
}

const normalizeBoardText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const BOARD_LOCATION_GROUPS = [
  { label: "NACIONAL", aliases: ["NACIONAL", "NAC"] },
  { label: "FISCAL", aliases: ["FISCAL", "FIS"] },
  { label: "EA", aliases: ["EA"] },
  { label: "ANEXO CORREO", aliases: ["ANEXO CORREO", "ANEXO", "CORREO"] },
  { label: "SIN UBICACION", aliases: ["SIN UBICACION"] },
];

function getBoardLocationGroup(value = "") {
  const normalized = normalizeBoardText(value);
  if (!normalized) {
    return BOARD_LOCATION_GROUPS[BOARD_LOCATION_GROUPS.length - 1];
  }
  return (
    BOARD_LOCATION_GROUPS.find((group) => group.aliases.includes(normalized)) ||
    null
  );
}

function buildBoardLocationConfig(ocupaciones = []) {
  const exactNames = new Map();
  const namesByGroup = new Map();

  (Array.isArray(ocupaciones) ? ocupaciones : []).forEach((item) => {
    const displayName = String(item?.nombre || "").trim();
    if (!displayName) return;

    exactNames.set(normalizeBoardText(displayName), displayName);

    const group = getBoardLocationGroup(displayName);
    if (group && !namesByGroup.has(group.label)) {
      namesByGroup.set(group.label, displayName);
    }
  });

  return {
    exactNames,
    namesByGroup,
  };
}

function resolveBoardLocationDisplay(value = "", config = null) {
  const normalized = normalizeBoardText(value);
  if (!normalized) {
    return config?.namesByGroup?.get("SIN UBICACION") || "SIN UBICACION";
  }

  if (config?.exactNames?.has(normalized)) {
    return config.exactNames.get(normalized);
  }

  const group = getBoardLocationGroup(normalized);
  if (group && config?.namesByGroup?.has(group.label)) {
    return config.namesByGroup.get(group.label);
  }

  return group?.label || normalized;
}

function getPalletClienteNombre(item) {
  const ingreso = item?.ingreso || {};
  return (
    String(
      ingreso?.clienteNombre ||
        ingreso?.cliente?.nombreCliente ||
        item?.clienteNombre ||
        item?.cliente?.nombreCliente ||
        item?.nombreCliente ||
        "Sin cliente"
    ).trim() || "Sin cliente"
  );
}

function palletCuentaEnOcupacion(item) {
  if (!item || item.cuentaPosicion === false) return false;

  const cantidad = Number(item?.cantidad);
  const egresado = Number(item?.egresado);
  if (Number.isFinite(cantidad) && Number.isFinite(egresado)) {
    return Math.max(0, cantidad - egresado) > 0;
  }

  if (Number.isFinite(egresado)) {
    return egresado <= 0;
  }

  const disponible = Number(item?.disponible);
  if (Number.isFinite(disponible)) {
    return disponible > 0;
  }

  return !item?.fechaEgreso;
}

function getPalletSortTime(item) {
  const raw =
    item?.fechaIngreso ||
    item?.ingreso?.fechaIngreso ||
    item?.createdAt ||
    item?.updatedAt ||
    "";
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getPalletOcupacionLabel(item, boardConfig = null) {
  const raw =
    item?.ubicacionTexto ||
    item?.ingreso?.ubicacionTexto ||
    item?.ubicacion ||
    "";
  return resolveBoardLocationDisplay(raw, boardConfig);
}

function buildOcupacionDetalle(ocupacion, pallets, ocupacionesConfig = []) {
  const metrics = getOcupacionMetrics(ocupacion);
  const boardConfig = buildBoardLocationConfig(ocupacionesConfig);
  const ocupacionKey = normalizeBoardText(
    resolveBoardLocationDisplay(ocupacion?.nombre, boardConfig)
  );
  const posicionesActivas = [];
  const clientesMap = new Map();

  (Array.isArray(pallets) ? pallets : []).forEach((item) => {
    const ubicacionKey = normalizeBoardText(
      getPalletOcupacionLabel(item, boardConfig)
    );
    if (ubicacionKey !== ocupacionKey) return;
    if (!palletCuentaEnOcupacion(item)) return;

    const cliente = getPalletClienteNombre(item);
    posicionesActivas.push({ cliente, sortTime: getPalletSortTime(item) });

    const current = clientesMap.get(cliente) || {
      cliente,
      posiciones: 0,
      excedente: 0,
    };
    current.posiciones += 1;
    clientesMap.set(cliente, current);
  });

  const excedenteAsignado = Math.min(
    Number(metrics.excedente || 0),
    posicionesActivas.length
  );
  posicionesActivas
    .slice()
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, excedenteAsignado)
    .forEach((item) => {
      const current = clientesMap.get(item.cliente);
      if (current) {
        current.excedente += 1;
      }
    });

  const clientes = Array.from(clientesMap.values()).sort((a, b) => {
    if (b.posiciones !== a.posiciones) return b.posiciones - a.posiciones;
    if (b.excedente !== a.excedente) return b.excedente - a.excedente;
    return a.cliente.localeCompare(b.cliente, "es", { sensitivity: "base" });
  });

  return {
    clientes,
    posicionesDetectadas: posicionesActivas.length,
    excedenteAsignado,
  };
}

function getEstadoCamion(op, camionIndex) {
  const conts = Array.isArray(op.Contenedores) ? op.Contenedores : [];
  const contCamion = conts.filter(
    (c) => (c.camionIndex || 0) === camionIndex
  );
  const todosContFinal =
    contCamion.length > 0 &&
    contCamion.every(
      (c) => (c.estado || "Completar") === "Finalizado"
    );

  const camion =
    Array.isArray(op.camiones) &&
    op.camiones.find((c) => c.index === camionIndex);

  const flagFinal = camion?.finalizado === true;
  const flagPend = camion?.pendiente === true;

  if (todosContFinal || flagFinal) return "FINALIZADO";
  if (flagPend) return "PENDIENTE";
  return "PROCESO";
}

// clasificar operación en una de las 4 tarjetas
function classifyGroup(op, deposito) {
  const tipo = String(op.tipoOperacion || "").trim().toLowerCase();
  const dep = (deposito || "binder1").toLowerCase();

  if (tipo.startsWith("nacional")) {
    return dep === "binder2" ? "nacional2" : "nacional1";
  }
  if (
    tipo.startsWith("fiscalimpo") ||
    tipo.startsWith("fiscal impo") ||
    tipo.startsWith("fiscal expo") ||
    tipo.startsWith("fiscalexpo")
  ) {
    return dep === "binder2" ? "fiscal2" : "fiscal1";
  }
  return null;
}

function classifyIngresoGroupKey(grupo, deposito) {
  const dep = resolveDepositoActual(deposito);
  const base = String(grupo || "").trim().toLowerCase() === "fiscal"
    ? "fiscal"
    : "nacional";
  return dep === "binder2" ? `${base}2` : `${base}1`;
}

/* ================== COMPONENTES UI ================== */

function Donut({ percent }) {
  const pct = Math.max(0, Math.min(100, percent || 0));
  const centerX = 60;
  const centerY = 60;
  const radius = 40;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const semiCircumference = circumference / 2;
  const progressLength = semiCircumference * (pct / 100);
  const angle = 180 - (pct / 100) * 180;
  const needleLength = radius - 10;
  const needleRadians = (angle * Math.PI) / 180;
  const needleX = centerX + needleLength * Math.cos(needleRadians);
  const needleY = centerY - needleLength * Math.sin(needleRadians);

  return (
    <div className="donut-wrap">
      <svg className="donut-meter" viewBox="0 0 120 80">
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="rgba(30,64,80,.6)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${semiCircumference} ${circumference}`}
          transform={`rotate(180 ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={strokeWidth}
          strokeDasharray={`${progressLength} ${circumference}`}
          transform={`rotate(180 ${centerX} ${centerY})`}
          strokeLinecap="round"
        />
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#d6ffe6"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r="7"
          fill="rgba(4,18,28,.95)"
          stroke="rgba(214,255,230,.45)"
          strokeWidth="2"
        />
        <circle cx={centerX} cy={centerY} r="3" fill="#d6ffe6" />
      </svg>

      <div className="donut-label">
        <span className="donut-value">{pct.toFixed(1)}%</span>
        <small style={{ fontSize: 11, color: "var(--muted)" }}>
          cerrados
        </small>
      </div>
    </div>
  );
}

function StatusCard({ title, data }) {
  const total = data?.total || 0; // camiones
  const cerrados = data?.cerrados || 0; // camiones finalizados
  const ingresos = data?.ingresos || 0; // camiones de ingreso
  const egresos = data?.egresos || 0; // camiones de egreso
  const comentarios = data?.comentarios || 0; // comentarios del día
  const pct = total > 0 ? (cerrados * 100) / total : 0;

  return (
    <div
      className="card status-card"
      style={{
        paddingTop: 10,
        paddingBottom: 10,
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* Donut arriba */}
      <div style={{ marginTop: 0, marginBottom: 2 }}>
        <Donut percent={pct} />
      </div>

      {/* Título debajo del gráfico */}
      <div style={{ marginTop: 0, marginBottom: 2 }}>
        <div
          className="card-title"
          style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}
        >
          {title}
        </div>
      </div>

      {/* Dos camiones + comentarios abajo */}
      <div
        className="card-metrics"
        style={{
          marginTop: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "center",
          fontSize: 13,
          color: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: "#fff",
          }}
        >
          <FiTruck style={{ transform: "scaleX(1)" }} />
          <strong>{ingresos}</strong>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: "#fff",
          }}
        >
          <FiTruck style={{ transform: "scaleX(-1)" }} />
          <strong>{egresos}</strong>
        </div>

        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            color: "#fff",
          }}
        >
          Comentarios: <strong>{comentarios}</strong>
        </div>
      </div>
    </div>
  );
}

function PositionBoard({ data, depositoLabel, loading }) {
  const normalizedBoard = normalizePositionBoardData(data);
  const columns = normalizedBoard.columns;
  const rows = normalizedBoard.rows;
  const totals = normalizedBoard.totals || {};
  const totalGeneral = Number(normalizedBoard.totalGeneral || 0);
  const shouldLimitRows = rows.length > 5;

  return (
    <div className="graph-panel">
      {loading && !rows.length ? (
        <div className="positions-empty">Cargando tablero de posiciones...</div>
      ) : rows.length ? (
        <div
          className={`positions-table-wrap${shouldLimitRows ? " positions-table-wrap--limited" : ""}`}
        >
          <table className="positions-table">
            <thead>
              <tr>
                <th>Cliente</th>
                {columns.map((column) => (
                  <th key={column} className="positions-number">
                    {column}
                  </th>
                ))}
                <th className="positions-number">Total posiciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.cliente}>
                  <td className="positions-client">{row.cliente}</td>
                  {columns.map((column) => (
                    <td key={`${row.cliente}-${column}`} className="positions-number">
                      {Number(row?.porUbicacion?.[column] || 0) || ""}
                    </td>
                  ))}
                  <td className="positions-number">
                    <strong>{Number(row.total || 0)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="positions-client">Total</td>
                {columns.map((column) => (
                  <td key={`total-${column}`} className="positions-number">
                    {Number(totals[column] || 0) || ""}
                  </td>
                ))}
                <td className="positions-number">{totalGeneral}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="positions-empty">
          No hay clientes con posiciones activas para {depositoLabel}.
        </div>
      )}
    </div>
  );
}

function OccupancyBoard({ depositoActual, ocupaciones, loading, onOpenDetail }) {
  const visibles = (ocupaciones || []).filter(
    (item) => item && item.visible !== false
  );
  const depositoLabel = binderName(depositoActual);

  return (
    <div className="graph-panel management-occupation-panel">
      {loading && !visibles.length ? (
        <div className="ocu-empty">Cargando factor ocupación...</div>
      ) : visibles.length ? (
        <div className="ocu-grid">
          {visibles.map((ocu) => {
            const { total, ocupadas, libres, excedente, pct, pctBar } =
              getOcupacionMetrics(ocu);

            return (
              <div
                key={ocu._id}
                className="ocu-card actionable"
                role="button"
                tabIndex={0}
                onClick={() => onOpenDetail?.(ocu)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpenDetail?.(ocu);
                  }
                }}
                title="Ver detalle por cliente"
              >
                <div className="ocu-title">{ocu.nombre}</div>
                <div className="ocu-sub">
                  Capacidad total: <strong>{total}</strong>
                </div>

                <div className="ocu-metrics">
                  <div>
                    <div className="ocu-pct">{pct.toFixed(0)}%</div>
                    <div className="ocu-sub">ocupación</div>
                  </div>
                  <div className="ocu-mini">
                    <div>
                      Ocupadas: <strong>{ocupadas}</strong>
                    </div>
                    <div>
                      Libres: <strong>{libres}</strong>
                    </div>
                    <div>
                      Excedente:{" "}
                      <strong style={{ color: excedente > 0 ? "#f87171" : "#fff" }}>
                        {excedente}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="ocu-bar" aria-hidden="true">
                  <div style={{ width: `${pctBar}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ocu-empty">
          No hay sucursales cargadas para {depositoLabel}.
        </div>
      )}
    </div>
  );
}

/* ================== PRINCIPAL ================== */

export default function InicioOperaciones() {
  useDashStyles();
  const { deposito } = useUserId();
  const depositoActual = resolveDepositoActual(deposito);
  const depositoLabel = binderName(depositoActual);
  const ocuRequestRef = useRef(0);
  const detalleOcupacionRequestRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null); // { fiscal1, fiscal2, nacional1, nacional2, global }
  const [, setSerie] = useState([]); // [{fecha, label, binder1Total, binder1Cerrados, binder2Total, binder2Cerrados}]
  const [truckSummary, setTruckSummary] = useState(null); // resumen camiones hoy
  const [ocuLoading, setOcuLoading] = useState(false);
  const [ocupaciones, setOcupaciones] = useState([]);
  const [detalleOcupacionOpen, setDetalleOcupacionOpen] = useState(false);
  const [detalleOcupacion, setDetalleOcupacion] = useState(null);
  const [detalleOcupacionData, setDetalleOcupacionData] = useState(null);
  const [detalleOcupacionLoading, setDetalleOcupacionLoading] = useState(false);
  const [detalleOcupacionError, setDetalleOcupacionError] = useState("");
  const [positionBoard, setPositionBoard] = useState({
    columns: [],
    rows: [],
    totals: {},
    totalGeneral: 0,
  });

  const hoyStr = fechaUTC();

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const hoy = new Date();
      const desdeDate = addDays(hoy, -6); // última semana
      const desdeStr = fechaUTC(desdeDate);

      const [resp1, resp2, ingresosBinder1, ingresosBinder2, boardResp] = await Promise.all([
        api.get("/api/operaciones/listar-rango", {
          params: { desde: desdeStr, hasta: hoyStr, deposito: "binder1" },
        }),
        api.get("/api/operaciones/listar-rango", {
          params: { desde: desdeStr, hasta: hoyStr, deposito: "binder2" },
        }),
        api.get("/api/ingresos/resumen-operativo", {
          params: { desde: desdeStr, hasta: hoyStr, deposito: "binder1" },
        }),
        api.get("/api/ingresos/resumen-operativo", {
          params: { desde: desdeStr, hasta: hoyStr, deposito: "binder2" },
        }),
        api.get("/api/pallets/tabla-posiciones", {
          params: { deposito: depositoActual },
        }),
      ]);

      const docs = []
        .concat(Array.isArray(resp1.data) ? resp1.data : [])
        .concat(Array.isArray(resp2.data) ? resp2.data : []);
      const ingresosResumen = []
        .concat(
          Array.isArray(ingresosBinder1?.data?.data)
            ? ingresosBinder1.data.data
            : []
        )
        .concat(
          Array.isArray(ingresosBinder2?.data?.data)
            ? ingresosBinder2.data.data
            : []
        );

      const boardData = boardResp?.data?.data || {};
      setPositionBoard({
        columns: Array.isArray(boardData.columns) ? boardData.columns : [],
        rows: Array.isArray(boardData.rows) ? boardData.rows : [],
        totals: boardData.totals || {},
        totalGeneral: Number(boardData.totalGeneral || 0),
      });

      const refIso = pickReferenciaFecha(
        docs.concat(ingresosResumen.map((item) => ({ fecha: item?.fecha }))),
        hoyStr
      );

      procesarDatos(docs, ingresosResumen, desdeStr, refIso);
    } catch (e) {
      console.error("Error cargando datos de operaciones", e);
      setStats(null);
      setSerie([]);
      setTruckSummary(null);
      setPositionBoard({
        columns: [],
        rows: [],
        totals: {},
        totalGeneral: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarOcupaciones = async () => {
    const requestId = ++ocuRequestRef.current;
    const storageKey = buildOcupacionesStorageKey(depositoActual);
    setOcuLoading(true);

    try {
      const arr = await apiListOcupaciones(depositoActual);
      if (ocuRequestRef.current !== requestId) return;
      setOcupaciones(arr);
      if (typeof window !== "undefined") {
        localStorage.setItem(storageKey, JSON.stringify(arr));
      }
    } catch {
      if (ocuRequestRef.current !== requestId) return;
      setOcupaciones(readStoredOcupaciones(storageKey, depositoActual));
    } finally {
      if (ocuRequestRef.current === requestId) {
        setOcuLoading(false);
      }
    }
  };

  const cerrarDetalleOcupacion = () => {
    detalleOcupacionRequestRef.current += 1;
    setDetalleOcupacionOpen(false);
    setDetalleOcupacion(null);
    setDetalleOcupacionData(null);
    setDetalleOcupacionLoading(false);
    setDetalleOcupacionError("");
  };

  const abrirDetalleOcupacion = async (ocu) => {
    const requestId = ++detalleOcupacionRequestRef.current;

    setDetalleOcupacionOpen(true);
    setDetalleOcupacion(ocu);
    setDetalleOcupacionData(null);
    setDetalleOcupacionLoading(true);
    setDetalleOcupacionError("");

    try {
      const { data } = await api.get("/api/pallets", {
        params: {
          deposito: depositoActual,
          page: 1,
          limit: 5000,
          sort: "-fechaIngreso",
          _ts: Date.now(),
        },
      });

      if (detalleOcupacionRequestRef.current !== requestId) return;

      const items = Array.isArray(data?.data?.items) ? data.data.items : [];
      setDetalleOcupacionData(buildOcupacionDetalle(ocu, items, ocupaciones));
    } catch (error) {
      if (detalleOcupacionRequestRef.current !== requestId) return;
      console.error("GET /api/pallets", error?.response || error);
      setDetalleOcupacionError(
        "No se pudo cargar el detalle por cliente para esta sucursal."
      );
    } finally {
      if (detalleOcupacionRequestRef.current === requestId) {
        setDetalleOcupacionLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    cargarDatos();
    cargarOcupaciones();
  };

  useEffect(() => {
    setOcupaciones(
      readStoredOcupaciones(
        buildOcupacionesStorageKey(depositoActual),
        depositoActual
      )
    );
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositoActual]);

  const procesarDatos = (docs, ingresosResumen, desdeStr, refIso) => {
    const emptyGroup = () => ({
      total: 0,
      cerrados: 0,
      ingresos: 0,
      egresos: 0,
      comentarios: 0,
    });

    const groups = {
      fiscal1: emptyGroup(),
      fiscal2: emptyGroup(),
      nacional1: emptyGroup(),
      nacional2: emptyGroup(),
    };

    const resumenHoy = {
      total: 0,
      pendientes: 0,
      proceso: 0,
      finalizados: 0,
      comentarios: 0,
    };

    const fallbackGroupTotals = {
      fiscal1: 0,
      fiscal2: 0,
      nacional1: 0,
      nacional2: 0,
    };
    const fallbackSeriesTotals = new Map();

    (Array.isArray(ingresosResumen) ? ingresosResumen : []).forEach((item) => {
      const fecha = String(item?.fecha || "").slice(0, 10);
      if (!fecha) return;

      const depositoIngreso = resolveDepositoActual(item?.deposito);
      const groupKey = classifyIngresoGroupKey(item?.grupo, depositoIngreso);
      const total = Math.max(0, Number(item?.total || 0));
      if (total <= 0) return;

      fallbackGroupTotals[groupKey] += total;
      const seriesKey = `${fecha}|${depositoIngreso}`;
      fallbackSeriesTotals.set(
        seriesKey,
        (fallbackSeriesTotals.get(seriesKey) || 0) + total
      );
    });

    const serieMap = {};
    let cursor = new Date(desdeStr);
    const end = addDays(new Date(refIso), 0);
    while (cursor <= end) {
      const iso = fechaUTC(cursor);
      serieMap[iso] = {
        fecha: iso,
        label: fmtLabel(iso),
        binder1Total: 0,
        binder1Cerrados: 0,
        binder2Total: 0,
        binder2Cerrados: 0,
      };
      cursor = addDays(cursor, 1);
    }


    docs.forEach((doc) => {
      const docFechaIso =
        typeof doc.fecha === "string"
          ? doc.fecha.slice(0, 10)
          : fechaUTC(doc.fecha || new Date());

      const dep = doc.deposito || "binder1";

      const daySerie =
        serieMap[docFechaIso] ||
        (serieMap[docFechaIso] = {
          fecha: docFechaIso,
          label: fmtLabel(docFechaIso),
          binder1Total: 0,
          binder1Cerrados: 0,
          binder2Total: 0,
          binder2Cerrados: 0,
        });

      const ops = Array.isArray(doc.Operacion) ? doc.Operacion : [];
        // Sumamos siempre dentro del rango (no solo refIso) para no perder categorías
        const esDiaDentro = docFechaIso === refIso;

        ops.forEach((op) => {
          const gKey = classifyGroup(op, dep);
          const grupo = gKey ? groups[gKey] : null;

        const esComentario =
          op.soloComentario === true ||
          op.esComentario === true ||
          (!Array.isArray(op.camiones) &&
            !op.cantidadCamiones &&
            !Array.isArray(op.Contenedores) &&
            (op.comentario || op.textoComentario || op.observacion));

        // Comentarios: se cuentan aparte y NO generan camiones
        if (esComentario) {
          if (esDiaDentro && grupo) grupo.comentarios += 1;
          if (esDiaDentro) resumenHoy.comentarios += 1;
          return;
        }

        const cantCamionesRaw =
          (Array.isArray(op.camiones) && op.camiones.length) ||
          Number(op.cantidadCamiones || 0);
        const cantCamiones = Math.max(0, cantCamionesRaw);

        // sin camiones â†’ nada que contar
        if (cantCamiones === 0) return;

        for (let i = 0; i < cantCamiones; i++) {
          const estado = getEstadoCamion(op, i);
          const esFinal = estado === "FINALIZADO";

          // Serie semanal por depósito
          const depKey = String(dep).toLowerCase() === "binder2" ? "binder2" : "binder1";
          daySerie[`${depKey}Total`] += 1;
          if (esFinal) daySerie[`${depKey}Cerrados`] += 1;

          // Tarjetas y global: ahora sumamos todo el rango visible
          if (esDiaDentro && grupo) {
            grupo.total += 1;
            if (esFinal) grupo.cerrados += 1;
            if (op.tipo === "Ingreso") grupo.ingresos += 1;
            else if (op.tipo === "Egreso") grupo.egresos += 1;
          }

          // Resumen (global)
          if (esDiaDentro) {
            resumenHoy.total += 1;
            if (estado === "FINALIZADO") resumenHoy.finalizados += 1;
            else if (estado === "PENDIENTE") resumenHoy.pendientes += 1;
            else resumenHoy.proceso += 1;
          }
        }
      });
    });

    Object.values(serieMap).forEach((row) => {
      if (!row.binder1Total) {
        row.binder1Total = fallbackSeriesTotals.get(`${row.fecha}|binder1`) || 0;
      }
      if (!row.binder2Total) {
        row.binder2Total = fallbackSeriesTotals.get(`${row.fecha}|binder2`) || 0;
      }
    });

    Object.entries(fallbackGroupTotals).forEach(([groupKey, total]) => {
      if (!groups[groupKey] || groups[groupKey].total > 0 || total <= 0) return;
      groups[groupKey].total = total;
      groups[groupKey].ingresos = total;
    });

    if (resumenHoy.total === 0) {
      const fallbackTodayTotal =
        (fallbackSeriesTotals.get(`${refIso}|binder1`) || 0) +
        (fallbackSeriesTotals.get(`${refIso}|binder2`) || 0);

      if (fallbackTodayTotal > 0) {
        resumenHoy.total = fallbackTodayTotal;
        resumenHoy.pendientes = fallbackTodayTotal;
      }
    }

    const globalTotal =
      groups.fiscal1.total +
      groups.fiscal2.total +
      groups.nacional1.total +
      groups.nacional2.total;
    const globalCerrados =
      groups.fiscal1.cerrados +
      groups.fiscal2.cerrados +
      groups.nacional1.cerrados +
      groups.nacional2.cerrados;
    const globalComentarios =
      groups.fiscal1.comentarios +
      groups.fiscal2.comentarios +
      groups.nacional1.comentarios +
      groups.nacional2.comentarios;

    setStats({
      ...groups,
      global: {
        total: globalTotal,
        cerrados: globalCerrados,
        comentarios: globalComentarios,
      },
    });

    const serieArr = Object.values(serieMap).sort((a, b) =>
      a.fecha.localeCompare(b.fecha)
    );
    setSerie(serieArr);
    setTruckSummary(resumenHoy);
  };

  const nationalStatusCard =
    depositoActual === "binder2"
      ? { title: "Nacional Binder 2", data: stats?.nacional2 }
      : { title: "Nacional Binder 1", data: stats?.nacional1 };
  const detalleOcupacionMetrics = getOcupacionMetrics(detalleOcupacion);

  return (
    <div className="dashboard-wrap">
      <div className="dash-header">
        <div className="dash-title">
          <h1>
            <FiTruck /> Status del día
          </h1>
          <small>Hoy: {hoyStr}</small>
        </div>
        <div className="dash-actions">
          <button className="dash-btn" onClick={handleRefresh}>
            <FiRefreshCw /> Actualizar
          </button>
        </div>
      </div>

      {truckSummary && (
        <section className="management-status-panel" aria-label="Estado nacional del día">
          <StatusCard
            key={nationalStatusCard.title}
            data={nationalStatusCard.data}
            title={nationalStatusCard.title}
          />
        </section>
      )}

      <div className="dashboard-top-sections">
        <div className="section-block positions-board-section">
          <h2 className="section-title">Tablero de posiciones</h2>
          <PositionBoard
            data={positionBoard}
            depositoLabel={depositoLabel}
            loading={loading}
          />
        </div>

        <div className="section-block occupancy-section">
          <h2 className="section-title">Factor ocupación</h2>
          <OccupancyBoard
            depositoActual={depositoActual}
            ocupaciones={ocupaciones}
            loading={ocuLoading}
            onOpenDetail={abrirDetalleOcupacion}
          />
        </div>
      </div>

      {truckSummary && (
        <div className="section-block">
          <h2 className="section-title">Resumen de camiones de hoy</h2>
          <div className="summary-layout">
            <div className="graph-panel summary-panel">
          <div className="graph-header">
            <div>
              <h2>Resumen de camiones de hoy</h2>
              <small>
                Distribución por estado de los camiones del día de referencia. Los
                comentarios no se incluyen como camiones.
              </small>
            </div>
          </div>

          <table className="truck-table">
            <thead>
              <tr>
                <th>Indicador</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Camiones hoy (total)</td>
                <td>
                  <strong>{truckSummary.total}</strong>
                </td>
              </tr>
              <tr>
                <td>Camiones pendientes</td>
                <td>
                  <strong>{truckSummary.pendientes}</strong>
                </td>
              </tr>
              <tr>
                <td>Camiones en proceso</td>
                <td>
                  <strong>{truckSummary.proceso}</strong>
                </td>
              </tr>
              <tr>
                <td>Camiones finalizados</td>
                <td>
                  <strong>{truckSummary.finalizados}</strong>
                </td>
              </tr>
              <tr>
                <td>Comentarios hoy</td>
                <td>
                  <strong>{truckSummary.comentarios}</strong>
                </td>
              </tr>
            </tbody>
          </table>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={detalleOcupacionOpen}
        title={
          detalleOcupacion
            ? `Detalle de ocupación · ${detalleOcupacion.nombre}`
            : "Detalle de ocupación"
        }
        onClose={cerrarDetalleOcupacion}
        width="min(780px, 96vw)"
        footer={
          <button
            className="dash-btn"
            type="button"
            onClick={cerrarDetalleOcupacion}
          >
            <FiX /> Cerrar
          </button>
        }
      >
        {detalleOcupacion && (
          <>
            <div className="ocu-detail-summary">
              <div className="ocu-detail-kpi">
                <span>Capacidad</span>
                <strong>{detalleOcupacionMetrics.total}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Ocupadas</span>
                <strong>{detalleOcupacionMetrics.ocupadas}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Libres</span>
                <strong>{detalleOcupacionMetrics.libres}</strong>
              </div>
              <div className="ocu-detail-kpi">
                <span>Excedente</span>
                <strong
                  style={{
                    color:
                      detalleOcupacionMetrics.excedente > 0
                        ? "#ff5a67"
                        : "var(--text)",
                  }}
                >
                  {detalleOcupacionMetrics.excedente}
                </strong>
              </div>
            </div>

            {detalleOcupacionMetrics.excedente > 0 && (
              <div className="ocu-detail-note">
                El excedente se reparte sobre las posiciones más recientes de la sucursal para identificar a qué clientes afecta.
              </div>
            )}

            {detalleOcupacionLoading && (
              <div className="ocu-detail-state">Cargando detalle por cliente…</div>
            )}

            {!detalleOcupacionLoading && detalleOcupacionError && (
              <div className="ocu-detail-state error">{detalleOcupacionError}</div>
            )}

            {!detalleOcupacionLoading &&
              !detalleOcupacionError &&
              detalleOcupacionData?.clientes?.length > 0 && (
                <>
                  <div className="ocu-detail-note">
                    Clientes con posiciones activas en {detalleOcupacion.nombre}. Posiciones detectadas:{" "}
                    <strong>{detalleOcupacionData.posicionesDetectadas}</strong>.
                  </div>

                  <div className="ocu-detail-table-wrap">
                    <table className="ocu-detail-table">
                      <thead>
                        <tr>
                          <th>Cliente</th>
                          <th>Posiciones</th>
                          <th>En excedente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalleOcupacionData.clientes.map((item) => (
                          <tr key={item.cliente}>
                            <td className="ocu-detail-client">{item.cliente}</td>
                            <td>
                              <strong>{item.posiciones}</strong>
                            </td>
                            <td>
                              <strong
                                className={
                                  item.excedente > 0 ? "ocu-detail-excess" : ""
                                }
                              >
                                {item.excedente}
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

            {!detalleOcupacionLoading &&
              !detalleOcupacionError &&
              detalleOcupacionData &&
              detalleOcupacionData.clientes.length === 0 && (
                <div className="ocu-detail-state">
                  No se encontraron posiciones activas para esta sucursal.
                </div>
              )}
          </>
        )}
      </Modal>

      {loading && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "var(--muted)",
          }}
        >
          Cargando datosâ€¦
        </div>
      )}
    </div>
  );
}
